import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Suppliers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
    gst_number: "",
    opening_balance: 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setList(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      mobile: "",
      email: "",
      address: "",
      gst_number: "",
      opening_balance: 0,
    });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...s });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim() || !form.mobile.trim()) {
        toast.error("Name & mobile required");
        return;
      }
      if (saving) return;
      setSaving(true);
      const payload = {
        ...form,
        opening_balance: Number(form.opening_balance) || 0,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.name = form.name;
      payload.mobile = form.mobile;
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    await api.delete(`/suppliers/${s.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="suppliers-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Procurement
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Suppliers
          </h1>
        </div>
        <Button
          data-testid="add-supplier-btn"
          onClick={openCreate}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New supplier
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Supplier
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                Mobile
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em]">
                GST
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Opening Bal.
              </TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.18em] text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground"
                >
                  No suppliers yet.
                </TableCell>
              </TableRow>
            ) : (
              list.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-gold">
                        <Truck size={14} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.email || s.address || "—"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{s.mobile}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {s.gst_number || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(s.opening_balance)}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => openEdit(s)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground mr-1"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(s)}
                      className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                    >
                      <Trash size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} Supplier
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { k: "name", label: "Name *" },
              { k: "mobile", label: "Mobile *" },
              { k: "email", label: "Email" },
              { k: "gst_number", label: "GST Number" },
              { k: "address", label: "Address" },
              {
                k: "opening_balance",
                label: "Opening Balance (₹)",
                type: "number",
              },
            ].map((f) => (
              <div
                key={f.k}
                className={f.k === "address" ? "md:col-span-2" : ""}
              >
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {f.label}
                </Label>
                <Input
                  data-testid={`field-supplier-${f.k}`}
                  type={f.type || "text"}
                  className="mt-1.5 bg-background border-border"
                  value={form[f.k] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-supplier-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
